/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Keywords.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-08-13
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-08-13   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  DialogContentText,
  IconButton,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import deleteIcon from "../../assets/delete_icon.png";
import { adminSettingsAPI, HandleApiActions } from "../../redux/actions";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";

const KEYWORD_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "EE Component", value: "2" },
  { label: "Software Module", value: "1" },
  { label: "Vehicle Component", value: "3" },
  { label: "Vehicle System", value: "6" },
  { label: "Legal", value: "5" },
];

const getKeywordList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  if (Array.isArray(resp?.data?.data)) return resp.data.data;
  return [];
};

const getKeywordTotalCount = (resp, list = []) =>
  resp?.total_count ??
  resp?.totalItems ??
  resp?.total ??
  resp?.data?.total_count ??
  (Array.isArray(list) ? list.length : 0);

const getKeywordTypeLabel = (type) =>
  KEYWORD_TYPE_OPTIONS.find((option) => option.value === type)?.label || type;

const formatKeywordType = (types = []) => {
  if (!Array.isArray(types)) {
    return types || "";
  }

  return types.map((type) => getKeywordTypeLabel(type)).join(", ");
};

function Keywords({ open, handleClick }) {
  const dispatch = useDispatch();
  const timeZoneDetails = useSelector((state) => state.profileReducer);
  const [tableKey, setTableKey] = useState(0);
  const [keywordList, setKeywordList] = useState([]);
  const [totalKeywordCount, setTotalKeywordCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [openAddKeyword, setOpenAddKeyword] = useState(false);
  const [openEditKeyword, setOpenEditKeyword] = useState(false);
  const [openDeleteKeyword, setOpenDeleteKeyword] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [name, setName] = useState("");
  const [types, setTypes] = useState([]);
  const [editName, setEditName] = useState("");
  const [editTypes, setEditTypes] = useState([]);
  const [originalEditName, setOriginalEditName] = useState("");
  const [originalEditTypes, setOriginalEditTypes] = useState([]);

  useEffect(() => {
    if (open === "keywords") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => setTableKey((prev) => prev + 1);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const normalizeKeywordRows = useCallback((list = []) => {
    return list.map((item, index) => {
      const itemTypes = item?.types || item?.type || [];
      const normalizedTypes = Array.isArray(itemTypes)
        ? itemTypes
        : String(itemTypes)
            .split(",")
            .map((type) => type.trim())
            .filter(Boolean);

      return {
        ...item,
        id: item?.id ?? item?.keyword_id ?? index + 1,
        name: item?.name || "",
        types: normalizedTypes,
        type: formatKeywordType(normalizedTypes),
        created_at: item?.created_at || item?.createdAt || "",
        updated_at: item?.updated_at || item?.updatedAt || "",
      };
    });
  }, []);

  const getKeywordDetails = useCallback(async (pageOverride, searchOverride) => {
    const pageToUse = pageOverride ?? page;
    const searchToUse = searchOverride ?? searchQuery;

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_keywords,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
            ...(searchToUse && { name: searchToUse }),
          },
        }),
      ),
    );

    if (resp?.error) {
      setKeywordList([]);
      setTotalKeywordCount(0);
      return;
    }

    const list = getKeywordList(resp);
    setKeywordList(normalizeKeywordRows(list));
    setTotalKeywordCount(getKeywordTotalCount(resp, list));
  }, [dispatch, normalizeKeywordRows, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    getKeywordDetails();
  }, [getKeywordDetails]);

  const handleEditClick = useCallback((params) => {
    const selectedTypes =
      params.row?.types ||
      params.row?.type
        ?.split(",")
        .map((item) => item.trim())
        .filter(Boolean) ||
      [];

    const individualValues = KEYWORD_TYPE_OPTIONS.filter(
      (option) => option.value !== "all",
    ).map((option) => option.value);

    const hasAll = individualValues.every((val) => selectedTypes.includes(val));
    const normalizedTypes = hasAll
      ? [...individualValues, "all"]
      : selectedTypes;

    setSelectedKeyword(params.row);
    setEditName(params.row?.name || "");
    setEditTypes(normalizedTypes);
    setOriginalEditName(params.row?.name || "");
    setOriginalEditTypes(normalizedTypes);
    setOpenEditKeyword(true);
  }, []);

  const handleDeleteClick = useCallback((params) => {
    setSelectedKeyword(params.row);
    setOpenDeleteKeyword(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
        sortable: false,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
        sortable: true,
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        renderCell: (params) => (
          <CustomSelectV2
            label={" "}
            required={false}
            optionsList={(params.row?.types || []).map((type) => ({
              label: getKeywordTypeLabel(type),
              value: type,
            }))}
            selectedValue={params.row?.types?.[0] || "No data"}
          />
        ),
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.6,
        minWidth: 130,
        sortable: true,
        renderCell: (params) => {
          if (params.value) {
            return convertUtcToTimeZone(
              params.value,
              timeZoneDetails?.profile?.profile?.region,
            );
          }
          return "N/A";
        },
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        flex: 0.6,
        minWidth: 130,
        sortable: true,
        renderCell: (params) => {
          if (params.value) {
            return convertUtcToTimeZone(
              params.value,
              timeZoneDetails?.profile?.profile?.region,
            );
          }
          return "N/A";
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params);
              }}
            >
              <img
                src={deleteIcon}
                alt="delete"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleDeleteClick, handleEditClick, timeZoneDetails],
  );

  const rows = useMemo(
    () =>
      keywordList.map((row, index) => ({
        ...row,
        sno: (page - 1) * rowsPerPage + index + 1,
      })),
    [keywordList, page, rowsPerPage],
  );

  const hasEditKeywordChanges = useMemo(() => {
    const normalizeTypes = (values = []) =>
      values
        .filter((type) => type !== "all")
        .map(String)
        .sort((a, b) => a.localeCompare(b));

    const currentTypes = normalizeTypes(editTypes);
    const originalTypes = normalizeTypes(originalEditTypes);
    const typesChanged =
      currentTypes.length !== originalTypes.length ||
      currentTypes.some((type, index) => type !== originalTypes[index]);

    return editName.trim() !== originalEditName.trim() || typesChanged;
  }, [editName, editTypes, originalEditName, originalEditTypes]);

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Keywords" },
  ];

  const resetAddForm = () => {
    setName("");
    setTypes([]);
  };

  const handleCloseAdd = () => {
    setOpenAddKeyword(false);
    resetAddForm();
  };

  const handleAddKeyword = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      name: name.trim(),
      types: types.filter((type) => type !== "all"),
    };

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.add_keywords,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (!resp?.error) {
        setPage(1);
        handleCloseAdd();
        await getKeywordDetails(1, searchQuery);
      }
    } catch (error) {
      console.error("Error adding keyword:", error);
    }
  };

  const handleUpdateKeyword = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      id: selectedKeyword?.id,
      name: editName.trim(),
      types: editTypes.filter((type) => type !== "all"),
    };

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.update_keywords,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (!resp?.error) {
        setOpenEditKeyword(false);
        setSelectedKeyword(null);
        await getKeywordDetails(page, searchQuery);
      }
    } catch (error) {
      console.error("Error updating keyword:", error);
    }
  };

  const handleDeleteKeywordConfirm = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      id: selectedKeyword?.id,
    };

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.delete_keywords,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (!resp?.error) {
        const nextPage = keywordList.length === 1 && page > 1 ? page - 1 : page;
        setOpenDeleteKeyword(false);
        setSelectedKeyword(null);
        setPage(nextPage);
        await getKeywordDetails(nextPage, searchQuery);
      }
    } catch (error) {
      console.error("Error deleting keyword:", error);
    }
  };

  const handleExportToExcel = () => {
    if (!keywordList || keywordList.length === 0) {
      console.warn("No data to export");
      return;
    }

    const exportData = keywordList.map((row, index) => ({
      "S.No": (page - 1) * rowsPerPage + index + 1,
      Name: row.name,
      Type: row.type,
      "Created At": row.created_at
        ? convertUtcToTimeZone(
            row.created_at,
            timeZoneDetails?.profile?.profile?.region,
          )
        : "N/A",
      "Updated At": row.updated_at
        ? convertUtcToTimeZone(
            row.updated_at,
            timeZoneDetails?.profile?.profile?.region,
          )
        : "N/A",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "Keywords");
    XLSX.writeFile(wb, "Keywords.xlsx");
  };

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              skipApiCall={true}
              getDataOnSearch={() => {}}
              onSearchQueryChange={(query) => {
                setSearchQuery(query);
                setPage(1);
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={() => setOpenAddKeyword(true)} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`keywords-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={rows}
          columns={columns}
          isMultiLine={true}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalKeywordCount}
        />
      </Box>

      <CustomDialogBoxV2
        open={openAddKeyword}
        onClose={handleCloseAdd}
        title={"Add Keyword"}
        successButtonName={"Add"}
        rejectButtonName={"Close"}
        onClickSuccess={handleAddKeyword}
        onDisabled={!name || name.trim().length < 3 || types.length === 0}
        actionButtonStyle={"continuous"}
        onClickReject={handleCloseAdd}
      >
        <Stack spacing={1.5} sx={{ width: "420px" }}>
          <CustomFormTextFieldV2
            label="Name"
            required={true}
            value={name}
            setValue={(val) => {
              if (val.length <= 50) {
                setName(val);
              }
            }}
            maxLength={50}
          />
          <KeywordTypeDropdown
            label="Type"
            required
            selectedValues={types}
            setSelectedValues={setTypes}
          />
        </Stack>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={openEditKeyword}
        onClose={() => setOpenEditKeyword(false)}
        title={"Update Keyword"}
        successButtonName={"Update"}
        rejectButtonName={"Close"}
        onClickSuccess={handleUpdateKeyword}
        onDisabled={
          !editName ||
          editName.trim().length < 3 ||
          editTypes.filter((type) => type !== "all").length === 0 ||
          !hasEditKeywordChanges
        }
        actionButtonStyle={"continuous"}
        onClickReject={() => setOpenEditKeyword(false)}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <CustomFormTextFieldV2
            label="Name"
            required={true}
            value={editName}
            setValue={(val) => {
              if (val.length <= 50) {
                setEditName(val);
              }
            }}
            maxLength={50}
          />
          <KeywordTypeDropdown
            label="Type"
            required
            selectedValues={editTypes}
            setSelectedValues={setEditTypes}
          />
        </Stack>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={openDeleteKeyword}
        onClose={() => setOpenDeleteKeyword(false)}
        title={"Delete Keyword"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleDeleteKeywordConfirm}
        onClickReject={() => setOpenDeleteKeyword(false)}
        actionButtonStyle={"continuous"}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default Keywords;

Keywords.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

function KeywordTypeDropdown({
  label,
  required = false,
  selectedValues,
  setSelectedValues,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedLabels = KEYWORD_TYPE_OPTIONS.filter((option) =>
    option.value !== "all" && selectedValues.includes(option.value),
  ).map((option) => option.label);

  const displayValue =
    selectedLabels.length > 0 ? selectedLabels.join(", ") : "select";

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value) => {
    const allValues = KEYWORD_TYPE_OPTIONS.map((option) => option.value);
    const individualValues = allValues.filter((optionValue) => optionValue !== "all");

    setSelectedValues((prev) => {
      if (value === "all") {
        return prev.includes("all") ? [] : allValues;
      }

      if (prev.includes(value)) {
        return prev.filter((item) => item !== value && item !== "all");
      }

      const nextValues = [...prev.filter((item) => item !== "all"), value];
      const hasEveryIndividualValue = individualValues.every((optionValue) =>
        nextValues.includes(optionValue),
      );

      return hasEveryIndividualValue ? allValues : nextValues;
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          mb: 0.5,
          color: "#6f7174",
          fontSize: "14px",
          fontWeight: 500,
          textAlign: "left",
        }}
      >
        {label}
        {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={displayValue}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: open ? (
            <KeyboardArrowUpIcon sx={{ color: "#000000" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: "#000000" }} />
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "32px",
            borderRadius: 0,
            "&:hover fieldset": { borderColor: "#000000" },
            "&.Mui-focused fieldset": { borderColor: "#000000" },
          },
          "& .MuiInputBase-input": {
            cursor: "pointer",
            fontSize: "14px",
            color: "#000000",
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: anchorEl?.clientWidth || 420,
              borderRadius: 0,
              boxShadow: "none",
              border: "1px solid #D0D0CE",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5, backgroundColor: "#ffffff" }}>
          {KEYWORD_TYPE_OPTIONS.map((option) => {
            const checked = selectedValues.includes(option.value);

            return (
              <MenuItem
                key={option.value}
                onClick={() => handleToggle(option.value)}
                sx={{
                  minHeight: "36px",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  "&:hover": { backgroundColor: "#F5F5F5" },
                }}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  sx={{
                    p: 0,
                    color: "#000000",
                    "&.Mui-checked": { color: "#26890D" },
                    "& .MuiSvgIcon-root": {
                      width: 22,
                      height: 22,
                    },
                  }}
                />
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: "14px",
                      color: "#1F1F1F",
                    },
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
}

KeywordTypeDropdown.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  setSelectedValues: PropTypes.func,
};
